import styled from '@emotion/styled';
import { t } from '@lingui/macro';
import { Box, Fade, Menu, MenuItem, type Theme } from '@mui/material';
import { Check, Warning } from '@phosphor-icons/react';
import {
  Avatar,
  AvatarGroup,
  Button,
  Chip,
  ChipProps,
  DebouncedInput,
  Text,
  TextProps,
} from 'components';
import { ComboBoxOption, ComboBoxApi } from 'components/inputs/types';
import { omit, debounce } from 'lodash';
import React, { useState, useCallback } from 'react';
import { styledTheme } from 'styles/theme/theme';
import { useGet } from 'lib/api';
import { parseSearch } from 'components/inputs/ComboBox';
export interface CustomDropDownProps {
  options: ComboBoxOption[];
  value?: Partial<ComboBoxOption>[];
  placeholder?: string;
  searchPlaceholder?: string;
  width?: number;
  enableSearch?: boolean;
  renderOption?: (option?: ComboBoxOption) => React.ReactNode;
  optionsChipRender?: (option: ComboBoxOption) => React.ReactNode;
  isInvalidOption?: (option: ComboBoxOption) => boolean;
  optionsChipProps?: ChipProps & {
    labelProps?: TextProps;
    sxWithOption?: (theme?: Theme, option?: ComboBoxOption) => object;
  };
  renderValue?: (value?: Partial<ComboBoxOption>[]) => React.ReactNode;
  api?: ComboBoxApi;
  optionsLimit?: number;
  atSearch?: boolean;
  noOptionsText?: React.ReactNode;
  onChange?: (
    event: React.SyntheticEvent,
    value: Partial<ComboBoxOption>[],
    reason: string
  ) => void;
  triggerStartIcon?: (open?: boolean) => React.ReactNode;
  triggerEndIcon?: (open?: boolean) => React.ReactNode;
}

const defaultValueRender = (value?: Partial<ComboBoxOption>[]) => {
  return (
    <AvatarGroup max={5} overlap="small">
      {value?.map(item => <Avatar key={item?.id} size="xsl" letters={item?.label} />)}
    </AvatarGroup>
  );
};

export const CustomDropDown = ({
  options = [],
  value: propsValue,
  placeholder = t`Select an option`,
  searchPlaceholder = t`Search`,
  width = 256,
  enableSearch = true,
  isInvalidOption,
  renderOption,
  optionsChipProps,
  optionsChipRender,
  renderValue = defaultValueRender,
  api = null,
  optionsLimit,
  atSearch = false,
  noOptionsText = t`No options`,
  onChange,
  triggerEndIcon,
  triggerStartIcon,
}: CustomDropDownProps) => {
  const [search, setSearch] = useState('');
  const [apiSearch, setApiSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const value = propsValue?.map(item => {
    const valueOption = options?.find(i => i.id === item?.id);
    return { ...valueOption, ...item };
  });

  // API data fetching
  const { data, isLoading } = useGet(
    api?.url || null,
    api?.search ? [api?.key, apiSearch] : api?.key || '',
    api?.search ? [parseSearch(atSearch, apiSearch), ...(api?.vars || [])] : [...(api?.vars || [])],
    undefined,
    api?.revalidate !== false,
    {
      enabled: api?.search ? Boolean(parseSearch(atSearch, apiSearch)) : !!api,
      ...(api?.options ?? {}),
    }
  );

  const selectOptions = (data && api ? api.responseTransform(data) : options) as ComboBoxOption[];

  // Debounced search for API calls
  const inputChangeApi = useCallback(
    debounce((newInputValue: string) => {
      setApiSearch(newInputValue);
    }, 250),
    []
  );

  // Filter options based on search value
  const filteredOptions = (() => {
    const optionsToFilter = selectOptions;

    if (!enableSearch) {
      return optionsToFilter;
    }

    if (api?.search) {
      // For API search, use the parsed search value from apiSearch state
      const parsedSearchValue = parseSearch(atSearch, apiSearch);
      if (!parsedSearchValue) return [];
      return optionsToFilter
        .filter(option => option.label.toLowerCase().includes(parsedSearchValue.toLowerCase()))
        .slice(0, optionsLimit);
    }

    // For local search, use the search state (immediate filtering)
    return optionsToFilter.filter(option =>
      option.label.toLowerCase().includes(search.toLowerCase())
    );
  })();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (enableSearch) {
      setSearch('');
      setApiSearch('');
    }
  };

  const handleOptionClick = (option: ComboBoxOption, event: React.MouseEvent) => {
    if (isInvalidOption?.(option)) {
      return; // Don't allow selection of invalid options
    }

    const currentValue = value || [];
    const isSelected = currentValue.find(item => item?.id === option.id);

    let newValue: Partial<ComboBoxOption>[];
    let reason: string;

    if (isSelected) {
      // Remove the option (deselect)
      newValue = currentValue.filter(item => item?.id !== option.id);
      reason = 'removeOption';
    } else {
      // Add the option (select)
      newValue = [...currentValue, option];
      reason = 'selectOption';
    }

    // Call onChange with the new value
    onChange?.(event, newValue, reason);
  };

  return (
    <>
      <Button
        sx={{
          padding: '9px 14px',
          borderRadius: '8px',
          border: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
          background: styledTheme.palette.bg.primary,
          '&:hover': {
            background: styledTheme.palette.bg.primary,
            border: `0.5px solid ${styledTheme.palette.border.brand}`,
          },
          justifyContent: 'space-between',
          textTransform: 'none',
          width,
          ...(open
            ? {
                border: `0.5px solid ${styledTheme.palette.border.brand}`,
                background: styledTheme.palette.bg.primary,
                boxShadow: `0 1px 2px 0 rgba(16, 24, 40, 0.05), 0 0 0 4px rgba(22, 105, 99, 0.08)`,
              }
            : {}),
        }}
        id="custom-dropdown-trigger"
        data-testid="custom-dropdown-trigger"
        aria-controls={open ? 'custom-dropdown-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : 'false'}
        size="lg"
        onClick={handleClick}
        variant="tertiaryOutline"
        startIcon={triggerStartIcon?.(open)}
        endIcon={triggerEndIcon?.(open)}
      >
        <Text variant="bodyMd" color={styledTheme.palette.text.placeholder}>
          {value?.length ? renderValue?.(value) : placeholder}
        </Text>
      </Button>
      {open && (
        <Menu
          id="custom-dropdown-menu"
          data-testid="custom-dropdown-menu"
          anchorEl={anchorEl}
          slotProps={{
            paper: {
              sx: {
                maxWidth: width,
                minWidth: width,
                maxHeight: '326px',
                marginTop: '4px',
                borderRadius: '8px',
                border: `0.5px solid ${styledTheme.palette.border.primarySubtle}`,
                boxShadow:
                  '0px 12px 16px -4px rgba(16, 24, 40, 0.08), 0px 4px 6px -2px rgba(16, 24, 40, 0.03)',
                ul: {
                  maxHeight: '316px',
                  overflow: 'hidden',
                  li: { marginBlock: 1 },
                },
              },
            },
          }}
          open={open}
          onClose={handleClose}
          TransitionComponent={Fade}
          MenuListProps={{
            sx: {
              padding: '6px',
            },
          }}
        >
          {enableSearch && (
            <li style={{ display: 'flex', justifyContent: 'center' }}>
              <DebouncedInput
                data-testid="custom-dropdown-search"
                onKeyDown={e => {
                  e.stopPropagation();
                }}
                sx={{ width: 'unset', input: { padding: '6px 10px' } }}
                placeholder={searchPlaceholder}
                value={search}
                onChange={v => {
                  setSearch(v);
                  if (api?.search) {
                    inputChangeApi(v);
                  }
                }}
              />
            </li>
          )}
          {isLoading && api?.search && (
            <CustomDropDownMenuItem
              disabled
              style={{ display: 'flex', justifyContent: 'center', padding: '8px' }}
            >
              <Text variant="bodySm" color={styledTheme.palette.text.placeholder}>
                {t`Loading...`}
              </Text>
            </CustomDropDownMenuItem>
          )}
          {!isLoading && filteredOptions.length === 0 && (
            <CustomDropDownMenuItem
              disabled
              style={{ display: 'flex', justifyContent: 'center', padding: '8px' }}
            >
              <Text variant="bodySm" color={styledTheme.palette.text.placeholder}>
                {noOptionsText}
              </Text>
            </CustomDropDownMenuItem>
          )}
          <CustomDropDownMenuItemWrapper>
            {filteredOptions?.map(option => {
              const isSelected = value?.find(item => item?.id === option.id);
              const isInvalid = isInvalidOption?.(option);
              const textColor = isInvalid
                ? styledTheme.palette.text.disabled
                : styledTheme.palette.text.secondary;

              return (
                <CustomDropDownMenuItem
                  key={option.id}
                  data-testid={`custom-dropdown-option-${option.id}`}
                  onClick={event => handleOptionClick(option, event)}
                  disabled={isInvalid}
                  sx={{
                    display: 'flex',
                    width: '100%',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    flexDirection: 'row',
                    padding: '6px 10px',
                    gap: '8px',
                    marginBlock: '1px',
                    borderRadius: '4px',
                    color: `${textColor} !important`,
                    fill: `${textColor} !important`,
                    cursor: isInvalid ? 'not-allowed' : 'pointer',
                    '& span, & div': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    },
                    ...(isInvalid ? { '& .MuiAvatar-root': { opacity: 0.5 } } : {}),
                    ...(isSelected
                      ? {
                          background: styledTheme?.palette?.bg?.brandSubtle,
                          fontWeight: 600,
                          '& span, & div': {
                            fontWeight: 600,
                          },
                        }
                      : {}),
                  }}
                >
                  {isInvalid && (
                    <Warning
                      color="inherit"
                      size={24}
                      style={{ minWidth: '24px', minHeight: '24px' }}
                    />
                  )}
                  <span
                    style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  >
                    {renderOption ? renderOption(option) : option.label}
                  </span>

                  {(optionsChipRender || optionsChipProps?.label) && (
                    <Chip
                      isTooltip={false}
                      size="sm"
                      label={optionsChipRender?.(option)}
                      {...omit(optionsChipProps, ['labelProps'])}
                      sx={themeSX => {
                        return {
                          marginLeft: 'auto',
                          ...optionsChipProps?.sxWithOption?.(themeSX, option),
                        };
                      }}
                    />
                  )}
                  {isSelected && (
                    <Check
                      size={20}
                      style={{
                        minWidth: '20px',
                        minHeight: '20px',
                        ...(!(optionsChipRender || optionsChipProps?.label)
                          ? { marginLeft: 'auto' }
                          : {}),
                      }}
                      color={styledTheme?.palette?.primary?.light}
                    />
                  )}
                </CustomDropDownMenuItem>
              );
            })}
          </CustomDropDownMenuItemWrapper>
        </Menu>
      )}
    </>
  );
};

const CustomDropDownMenuItem = styled(MenuItem)`
  min-height: 40px !important;
`;

const CustomDropDownMenuItemWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  overflow: auto;
  max-height: 248px;
`;

export default CustomDropDown;
